import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { Dialog, DialogContent, DialogTitle } from '../components/ui/dialog';
import { X, ZoomIn, Filter, ChevronLeft, ChevronRight } from 'lucide-react';
import axios from 'axios';
import { normalizeImageUrl } from '../lib/imageUrl';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GALLERY_HERO = "https://images.unsplash.com/photo-1562774053-701939374585?w=1920&q=80";

// Sample gallery data
const sampleGallery = [
  { id: '1', title: 'College Building', category: 'campus', image_url: 'https://images.unsplash.com/photo-1562774053-701939374585?w=800&q=80', description: 'Main academic building' },
  { id: '2', title: 'Teaching Hospital', category: 'hospital', image_url: 'https://images.unsplash.com/photo-1586773860418-d37222d8fce3?w=800&q=80', description: 'Multi-specialty teaching hospital' },
  { id: '3', title: 'Laboratory', category: 'academics', image_url: 'https://images.unsplash.com/photo-1582719471384-894fbb16e074?w=800&q=80', description: 'Modern laboratory facilities' },
  { id: '4', title: 'Library', category: 'academics', image_url: 'https://images.unsplash.com/photo-1507842217343-583bb7270b66?w=800&q=80', description: 'Central library' },
  { id: '5', title: 'Hostel Block', category: 'hostel', image_url: 'https://images.unsplash.com/photo-1555854877-bab0e564b8d5?w=800&q=80', description: 'Student hostel facilities' },
  { id: '6', title: 'Sports Ground', category: 'campus', image_url: 'https://images.unsplash.com/photo-1461896836934-3c8f3d0e6e8e?w=800&q=80', description: 'Outdoor sports facilities' },
  { id: '7', title: 'Lecture Hall', category: 'academics', image_url: 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=800&q=80', description: 'Modern lecture halls' },
  { id: '8', title: 'Operation Theatre', category: 'hospital', image_url: 'https://images.unsplash.com/photo-1579684288452-b334934f845f?w=800&q=80', description: 'Advanced surgical facilities' },
  { id: '9', title: 'Campus View', category: 'campus', image_url: 'https://images.unsplash.com/photo-1523050854058-8df90110c9f1?w=800&q=80', description: 'Aerial campus view' },
  { id: '10', title: 'Student Common Area', category: 'hostel', image_url: 'https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?w=800&q=80', description: 'Recreation area' },
];

const categories = [
  { id: 'all', name: 'All Photos' },
  { id: 'campus', name: 'Campus' },
  { id: 'hospital', name: 'Hospital' },
  { id: 'academics', name: 'Academics' },
  { id: 'hostel', name: 'Hostel' },
  { id: 'events', name: 'Events' },
];

const Gallery = () => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    fetchGallery();
  }, []);

  const fetchGallery = async () => {
    try {
      const response = await axios.get(`${API}/gallery`);
      if (response.data && response.data.length > 0) {
        setImages(response.data);
      } else {
        setImages(sampleGallery);
      }
    } catch (error) {
      console.error('Error fetching gallery:', error);
      setImages(sampleGallery);
    } finally {
      setLoading(false);
    }
  };

  const filteredImages = selectedCategory === 'all'
    ? images
    : images.filter(img => img.category === selectedCategory);

  const selectedIndex = selectedImage ? filteredImages.findIndex(i => i.id === selectedImage.id) : -1;
  const goPrev = () => {
    if (selectedIndex < 0 || filteredImages.length === 0) return;
    const next = (selectedIndex - 1 + filteredImages.length) % filteredImages.length;
    setSelectedImage(filteredImages[next]);
  };
  const goNext = () => {
    if (selectedIndex < 0 || filteredImages.length === 0) return;
    const next = (selectedIndex + 1) % filteredImages.length;
    setSelectedImage(filteredImages[next]);
  };

  // Keyboard arrow support + swipe (mobile)
  useEffect(() => {
    if (!selectedImage) return;
    const onKey = (e) => {
      if (e.key === 'ArrowLeft') goPrev();
      else if (e.key === 'ArrowRight') goNext();
      else if (e.key === 'Escape') setSelectedImage(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }); // intentionally re-bind each render so goPrev/goNext capture latest state

  return (
    <div className="page-transition" data-testid="gallery-page">
      {/* Hero Section */}
      <section 
        className="relative py-20 md:py-32 hero-overlay"
        style={{
          backgroundImage: `url(${GALLERY_HERO})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
        data-testid="gallery-hero"
      >
        <div className="container-custom">
          <div className="max-w-3xl">
            <span className="inline-block bg-white/20 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm font-medium mb-6">
              Photo Gallery
            </span>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl text-white font-bold leading-tight mb-6">
              Campus Gallery
            </h1>
            <p className="text-lg md:text-xl text-white/90 leading-relaxed">
              Explore our campus, facilities, and vibrant student life through our photo gallery.
            </p>
          </div>
        </div>
      </section>

      {/* Filter Bar */}
      <section className="py-6 bg-white border-b border-gray-100" data-testid="gallery-filter">
        <div className="container-custom">
          <div className="flex flex-wrap items-center gap-3">
            <Filter size={18} className="text-gray-500" />
            {categories.map((category) => (
              <Button
                key={category.id}
                variant={selectedCategory === category.id ? 'default' : 'outline'}
                size="sm"
                className={`rounded-full ${
                  selectedCategory === category.id
                    ? 'bg-[#4ECDC4] hover:bg-[#3DBDB4] text-white'
                    : 'border-gray-200 text-gray-600 hover:border-[#4ECDC4] hover:text-[#4ECDC4]'
                }`}
                onClick={() => setSelectedCategory(category.id)}
                data-testid={`gallery-filter-${category.id}`}
              >
                {category.name}
              </Button>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="section-padding bg-[#F8F9FA]" data-testid="gallery-grid">
        <div className="container-custom">
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                <Skeleton key={i} className="aspect-square rounded-xl" />
              ))}
            </div>
          ) : filteredImages.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-gray-500 text-lg">No photos found in this category.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {filteredImages.map((image, index) => (
                <Card
                  key={image.id}
                  className="group cursor-pointer overflow-hidden border-0 shadow-sm hover:shadow-lg transition-all duration-300"
                  onClick={() => setSelectedImage(image)}
                  data-testid={`gallery-item-${index}`}
                >
                  <div className="relative aspect-square overflow-hidden">
                    <img
                      src={normalizeImageUrl(image.image_url)}
                      alt={image.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all duration-300 flex items-center justify-center">
                      <ZoomIn className="text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" size={32} />
                    </div>
                  </div>
                  <CardContent className="p-3">
                    <p className="font-medium text-[#1A202C] text-sm truncate">{image.title}</p>
                    <p className="text-xs text-gray-500 capitalize">{image.category}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Lightbox Dialog */}
      <Dialog open={!!selectedImage} onOpenChange={() => setSelectedImage(null)}>
        <DialogContent className="max-w-5xl p-0 bg-black border-0">
          <DialogTitle className="sr-only">
            {selectedImage?.title || 'Gallery Image'}
          </DialogTitle>
          {selectedImage && (
            <div
              className="relative"
              onTouchStart={e => { window.__sx = e.touches[0].clientX; }}
              onTouchEnd={e => {
                const dx = (e.changedTouches[0].clientX - (window.__sx || 0));
                if (Math.abs(dx) > 50) (dx > 0 ? goPrev : goNext)();
              }}
            >
              <button
                onClick={() => setSelectedImage(null)}
                className="absolute top-4 right-4 z-20 w-10 h-10 bg-black/50 rounded-full flex items-center justify-center text-white hover:bg-black/70 transition-colors"
                data-testid="lightbox-close"
                aria-label="Close"
              >
                <X size={20} />
              </button>
              {filteredImages.length > 1 && (
                <>
                  <button
                    onClick={goPrev}
                    className="absolute left-3 top-1/2 -translate-y-1/2 z-20 w-12 h-12 bg-black/50 rounded-full flex items-center justify-center text-white hover:bg-black/70 transition-colors"
                    data-testid="lightbox-prev"
                    aria-label="Previous image"
                  >
                    <ChevronLeft size={24} />
                  </button>
                  <button
                    onClick={goNext}
                    className="absolute right-3 top-1/2 -translate-y-1/2 z-20 w-12 h-12 bg-black/50 rounded-full flex items-center justify-center text-white hover:bg-black/70 transition-colors"
                    data-testid="lightbox-next"
                    aria-label="Next image"
                  >
                    <ChevronRight size={24} />
                  </button>
                </>
              )}
              <img
                src={normalizeImageUrl(selectedImage.image_url)}
                alt={selectedImage.title}
                className="w-full h-auto max-h-[80vh] object-contain select-none"
                draggable={false}
              />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6 flex items-end justify-between gap-4">
                <div>
                  <h3 className="text-white font-heading text-xl font-semibold">
                    {selectedImage.title}
                  </h3>
                  {selectedImage.description && (
                    <p className="text-white/80 text-sm mt-1">{selectedImage.description}</p>
                  )}
                </div>
                {filteredImages.length > 1 && (
                  <span className="text-white/70 text-xs whitespace-nowrap" data-testid="lightbox-counter">
                    {selectedIndex + 1} / {filteredImages.length}
                  </span>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Gallery;
